import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Brandmark, Wordmark } from "./Brand";
import { api, getActiveArcherId } from "../lib/api";
import { useArrowLog } from "../lib/arrowLog";
import { useApiData } from "../lib/hooks";
import { useCounterResume } from "../lib/useCounterResume";
import { buildEndLabel } from "../hooks/useScoringSession";
import { FeedbackModal } from "./FeedbackModal";

// ─── Icon set (1.7–1.8px stroke, archery-literal where earned) ────────────
function NavIcon({ k }) {
  const s = { fill: "none", stroke: "currentColor", strokeLinecap: "round", strokeLinejoin: "round" };
  switch (k) {
    case "home":
      return <svg viewBox="0 0 24 24" {...s}><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5.5" /><circle cx="12" cy="12" r="2" /><circle cx="12" cy="12" r="0.4" fill="currentColor" /></svg>;
    case "arrow":
      return <svg viewBox="0 0 24 24" {...s}><path d="M4 20 L20 4" /><path d="M14 4 L20 4 L20 10" /><path d="M5 13 C 8 16, 11 19, 12 20" /><circle cx="12" cy="20" r="0.6" fill="currentColor" /></svg>;
    case "clock":
      return <svg viewBox="0 0 24 24" {...s}><circle cx="12" cy="12" r="9" /><path d="M12 7 v5 l3.5 2" /></svg>;
    case "target":
      return <svg viewBox="0 0 24 24" {...s}><circle cx="12" cy="12" r="3" fill="currentColor" /><circle cx="12" cy="12" r="7" /><path d="M2 12 h2 M20 12 h2 M12 2 v2 M12 20 v2" /></svg>;
    case "medal":
      return <svg viewBox="0 0 24 24" {...s}><path d="M8 4 l4 7 l4 -7" /><circle cx="12" cy="16" r="5" /><path d="M12 13 v2 M11 16 h2" /></svg>;
    case "cal":
      return <svg viewBox="0 0 24 24" {...s}><rect x="4" y="5" width="16" height="15" rx="2" /><path d="M4 9 h16 M9 3 v4 M15 3 v4" /><circle cx="12" cy="14" r="2" /></svg>;
    case "list":
      return <svg viewBox="0 0 24 24" {...s}><path d="M4 6 h8 M4 12 h12 M4 18 h6" /><circle cx="18" cy="18" r="2.5" /></svg>;
    case "chart":
      return <svg viewBox="0 0 24 24" {...s}><path d="M4 20 V14 M9 20 V9 M14 20 V12 M19 20 V5" strokeWidth="1.8" strokeLinecap="round" /></svg>;
    case "tally":
      // Tally marks — counting arrows, not scoring them (same glyph as Home's card).
      return <svg viewBox="0 0 24 24" {...s}><path d="M6 5v14M11 5v14M16 5v14" /><path d="M3.5 16.5 19 7" /></svg>;
    case "compass":
      return <svg viewBox="0 0 24 24" {...s}><circle cx="12" cy="12" r="9" /><path d="M16.24 7.76 l-2.12 6.36 -6.36 2.12 2.12-6.36 6.36-2.12z" /></svg>;
    case "club":
      return <svg viewBox="0 0 24 24" {...s}><path d="M12 2 C10 5,7 5,7 8 a3 3 0 0 0 3 3 c-1 2,-2 3,-3 4 h8 c-1-1,-2-2,-3-4 a3 3 0 0 0 3-3 c0-3,-3-3,-5-6z" /></svg>;
    case "bow":
      return <svg viewBox="0 0 24 24" {...s}><path d="M6 3 Q18 12 6 21" /><path d="M6 7 L18 12 L6 17" strokeWidth="1.2" opacity=".5" /></svg>;
    case "settings":
      return <svg viewBox="0 0 24 24" {...s}><circle cx="12" cy="12" r="3" /><path d="M12 2v2m0 16v2M4.93 4.93l1.41 1.41m11.32 11.32 1.41 1.41M2 12h2m16 0h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" strokeWidth="1.6" /></svg>;
    case "user":
      return <svg viewBox="0 0 24 24" {...s}><circle cx="12" cy="8.5" r="3.5" /><path d="M5.5 20c0-3.6 2.9-6 6.5-6s6.5 2.4 6.5 6" /></svg>;
    default:
      return null;
  }
}

// ─── Nav definition ────────────────────────────────────────────────────────
// `countKey` is matched against the counts object from useSidebarCounts().
const NAV = [
  {
    group: "",
    items: [
      { to: "/",        label: "Home",    icon: "home",   end: true },
      { to: "/new-round", label: "Scoring", icon: "target", live: true },
      // Action item, not a route (#1943): opens the global Log-arrows sheet.
      { action: "log-arrows", label: "Log arrows", icon: "tally" },
      { to: "/history", label: "History", icon: "clock" },
      { to: "/stats",   label: "Stats",   icon: "chart" },
    ],
  },
  {
    group: "Events",
    items: [
      { to: "/rbte", label: "RBTE", icon: "list", feature: "rbte" },
      { to: "/tournaments", label: "Tournaments", icon: "cal",  disabled: true },
      { to: "/leagues",     label: "Leagues",     icon: "list", disabled: true },
    ],
  },
  {
    group: "Manage",
    items: [
      { to: "/profile",   label: "Profile",   icon: "user" },
      { to: "/equipment", label: "Equipment", icon: "bow" },
      { to: "/settings",  label: "Settings",  icon: "settings" },
    ],
  },
];

// ─── The archer's resumable in-progress round, if any ──────────────────────
function useActiveRound() {
  const location = useLocation();
  // Refetch on navigation so the card appears/clears as rounds start & end.
  const { data } = useApiData(
    () => api.rounds({ active: true, limit: 1 }),
    [location.pathname],
  );
  // The error is deliberately dropped here, unlike Practice/Round: this is
  // chrome, and a failed poll should silently leave the resume card hidden
  // rather than put an error banner in the nav (#2068).
  return data?.[0] || null;
}

// ─── Sidebar ───────────────────────────────────────────────────────────────
export function Sidebar({ activeSession = null, mobileOpen = false, onClose }) {
  const location = useLocation();
  const scoring  = location.pathname.startsWith("/score") || location.pathname === "/new-round";
  const activeRound = useActiveRound();
  // In-progress burst-tap counter session (#1814) — coexists with the
  // resume-round card below; starting/scoring a round does not end a
  // counter session, and vice versa.
  const counterSummary = useCounterResume(getActiveArcherId());
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const { openBlank: openLogSheet } = useArrowLog();
  const { hasFeature } = useAuth();

  const showScoringCard = scoring && activeSession;
  const showResumeRound = !showScoringCard && activeRound;
  const showCounter = Boolean(counterSummary);

  return (
    <>
      {/* On narrow screens the sidebar is a drawer over a tap-to-close scrim. */}
      {mobileOpen && <div className="nav-backdrop" onClick={onClose} />}
      <aside className={"side" + (mobileOpen ? " side--open" : "")}>
      <div className="side-brand">
        <div className="brand-mark"><Brandmark /></div>
        <Wordmark />
      </div>

      <nav className="side-nav">
        {NAV.map((g, i) => (
          <div className="nav-group" key={i}>
            {g.group && <div className="nav-label">{g.group}</div>}
            {g.items.map((it) => {
              if (it.feature && !hasFeature(it.feature)) return null;
              if (it.disabled) {
                return (
                  <span key={it.to} className="nav-item nav-item--disabled">
                    <NavIcon k={it.icon} />
                    {it.label}
                  </span>
                );
              }
              if (it.action === "log-arrows") {
                // Button, not NavLink: it opens the global Log-arrows sheet and
                // has no route, so it must never carry active-link styling.
                return (
                  <button
                    key={it.action}
                    type="button"
                    className="nav-item"
                    onClick={() => {
                      openLogSheet();
                      onClose?.(); // close the mobile drawer under the sheet
                    }}
                  >
                    <NavIcon k={it.icon} />
                    {it.label}
                  </button>
                );
              }
              return (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.end}
                  className={({ isActive }) => "nav-item" + (isActive ? " active" : "")}
                >
                  <NavIcon k={it.icon} />
                  {it.label}
                  {it.live && <span className="live-pip" />}
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>

      {(showScoringCard || showResumeRound || showCounter) ? (
        <div className="side-foot">
          {showScoringCard && (
            <div className="session-card">
              <div className="pip" />
              <div className="label">SCORING SESSION</div>
              <h5>{activeSession.title}</h5>
              <p>{activeSession.subtitle}</p>
            </div>
          )}
          {showResumeRound && (
            <NavLink to={`/score?round=${activeRound.id}`} className="session-card resume-card">
              <div className="pip" />
              <div className="label">RESUME ROUND</div>
              <h5>{activeRound.event_name}</h5>
              <p>{buildEndLabel(activeRound)}</p>
            </NavLink>
          )}
          {showCounter && (
            <NavLink to="/count" className="session-card resume-card">
              <div className="pip" />
              <div className="label">COUNTING</div>
              <h5>{counterSummary.count} arrow{counterSummary.count === 1 ? "" : "s"}</h5>
              <p>Tap to resume</p>
            </NavLink>
          )}
        </div>
      ) : null}
      <button className="nav-feedback-btn" onClick={() => setFeedbackOpen(true)}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
        Feedback
      </button>
      </aside>

      {feedbackOpen && <FeedbackModal onClose={() => setFeedbackOpen(false)} />}
    </>
  );
}
